import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
import { signOut as firebaseSignOut, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/hooks/useTheme";
import { useToast } from "@/hooks/use-toast";
import {
  User,
  Shield,
  Bell,
  Settings,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Check,
  X,
  Edit2,
  Building2,
  Mail,
  Phone,
  Key,
  Eye,
  EyeOff,
} from "lucide-react";
const Profile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Profile editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    fullName: "",
    phone: "",
    companyName: "",
    customEmail: "",
  });
  const [savingField, setSavingField] = useState(false);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Notification preferences state
  const [notifications, setNotifications] = useState({
    emailJobUpdates: true,
    emailInspectionReminders: true,
    emailComplianceAlerts: true,
    emailWeeklySummary: false,
    pushEnabled: false,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Get active tab from URL or default to "account"
  const activeTab = searchParams.get("tab") || "account";

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate("/auth");
        return;
      }

      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const userData = { ...userDoc.data(), user_id: currentUser.uid } as any;
        setProfile(userData);

        // Initialize edit values
        setEditValues({
          fullName: userData.fullName || userData.full_name || "",
          phone: userData.phone || "",
          companyName: userData.companyName || userData.company_name || "",
          customEmail: userData.customEmail || userData.custom_email || "",
        });

        // Initialize notification preferences
        if (userData.notifications) {
          setNotifications(prev => ({ ...prev, ...userData.notifications }));
        }
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkAuth();
      } else {
        navigate("/auth");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSaveField = async (field: string) => {
    try {
      setSavingField(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const updateData: any = {};

      switch (field) {
        case "fullName":
          updateData.fullName = editValues.fullName.trim();
          break;
        case "phone":
          updateData.phone = editValues.phone.trim() || null;
          break;
        case "companyName":
          updateData.companyName = editValues.companyName.trim() || null;
          break;
        case "customEmail":
          updateData.customEmail = editValues.customEmail.trim() || null;
          break;
      }

      await updateDoc(doc(db, "users", currentUser.uid), updateData);
      setProfile({ ...profile, ...updateData });
      setEditingField(null);

      toast({
        title: "Saved",
        description: "Your changes have been saved.",
      });
    } catch (error: any) {
      console.error("Error updating field:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    } finally {
      setSavingField(false);
    }
  };

  const handleCancelEdit = (field: string) => {
    setEditValues(prev => ({
      ...prev,
      [field]: profile?.[field] || profile?.[field.replace(/([A-Z])/g, '_$1').toLowerCase()] || "",
    }));
    setEditingField(null);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    try {
      setChangingPassword(true);
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) return;

      // Re-authenticate first
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      toast({
        title: "Password Changed",
        description: "Your password has been updated successfully.",
      });

      // Reset form
      setShowPasswordChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast({
        title: "Error",
        description: error.code === "auth/wrong-password"
          ? "Current password is incorrect"
          : "Failed to change password",
        variant: "destructive",
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSavingNotifications(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      await updateDoc(doc(db, "users", currentUser.uid), {
        notifications,
      });

      toast({
        title: "Saved",
        description: "Notification preferences updated.",
      });
    } catch (error) {
      console.error("Error saving notifications:", error);
      toast({
        title: "Error",
        description: "Failed to save preferences",
        variant: "destructive",
      });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSignOut = async () => {
    await firebaseSignOut(auth);
    navigate("/auth");
  };

  const tradeLabels: Record<string, string> = {
    electrician: "Electrician",
    gas: "Natural/Propane Gas",
    plumber: "Plumber",
    hvac: "HVAC",
    other: "Other",
  };

  const displayTradeType = profile?.trade_type === "other" && profile?.custom_trade_type
    ? profile.custom_trade_type
    : tradeLabels[profile?.trade_type || profile?.tradeType || ""] || profile?.trade_type || profile?.tradeType;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const EditableField = ({
    field,
    label,
    value,
    icon: Icon,
    type = "text",
    placeholder = "",
  }: {
    field: string;
    label: string;
    value: string;
    icon: any;
    type?: string;
    placeholder?: string;
  }) => (
    <div className="flex items-start justify-between py-3">
      <div className="flex items-start gap-3 flex-1">
        <Icon className="h-5 w-5 text-muted-foreground mt-0.5" />
        <div className="flex-1">
          <Label className="text-sm text-muted-foreground">{label}</Label>
          {editingField === field ? (
            <div className="flex items-center gap-2 mt-1">
              <Input
                type={type}
                value={editValues[field as keyof typeof editValues]}
                onChange={(e) => setEditValues(prev => ({ ...prev, [field]: e.target.value }))}
                placeholder={placeholder}
                className="h-8"
              />
              <Button size="sm" variant="ghost" onClick={() => handleSaveField(field)} disabled={savingField}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleCancelEdit(field)} disabled={savingField}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <p className="text-sm font-medium mt-1">
              {value || <span className="text-muted-foreground italic">Not set</span>}
            </p>
          )}
        </div>
      </div>
      {editingField !== field && (
        <Button variant="ghost" size="sm" onClick={() => setEditingField(field)}>
          <Edit2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Settings" />
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="account" className="gap-2">
              <User className="h-4 w-4 hidden sm:block" />
              Account
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2">
              <Shield className="h-4 w-4 hidden sm:block" />
              Security
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4 hidden sm:block" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="preferences" className="gap-2">
              <Settings className="h-4 w-4 hidden sm:block" />
              Preferences
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            {/* Profile Information */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                <div className="flex items-start gap-3 py-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-sm text-muted-foreground">Full Name</Label>
                    <p className="text-sm font-medium mt-1">{profile?.fullName || profile?.full_name || "Not set"}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3 py-3">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-sm text-muted-foreground">Email Address</Label>
                    <p className="text-sm font-medium mt-1">{auth.currentUser?.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">Contact support to change your email</p>
                  </div>
                </div>
                <Separator />
                <EditableField
                  field="phone"
                  label="Phone Number (for 2FA)"
                  value={profile?.phone || ""}
                  icon={Phone}
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                />
                <p className="text-xs text-muted-foreground pl-8 -mt-2">
                  Used for two-factor authentication only. This is not shared with jurisdictions.
                </p>
                <Separator />
                <div className="flex items-start gap-3 py-3">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-sm text-muted-foreground">Trade Type</Label>
                    <p className="text-sm font-medium mt-1">{displayTradeType || "Not set"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sign Out */}
            <Card>
              <CardContent className="pt-6">
                <Button variant="outline" className="w-full" onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Password</CardTitle>
                <CardDescription>Change your account password</CardDescription>
              </CardHeader>
              <CardContent>
                {!showPasswordChange ? (
                  <Button variant="outline" onClick={() => setShowPasswordChange(true)}>
                    <Key className="mr-2 h-4 w-4" />
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 8 characters)"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleChangePassword} disabled={changingPassword}>
                        {changingPassword ? "Changing..." : "Update Password"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setCurrentPassword("");
                          setNewPassword("");
                          setConfirmPassword("");
                        }}
                        disabled={changingPassword}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Support Contact */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Support</CardTitle>
                <CardDescription>Need help with your account? Reach out to our support team.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>How can we help?</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    id="support-reason"
                    defaultValue=""
                  >
                    <option value="" disabled>Select a reason...</option>
                    <option value="billing">Billing inquiry</option>
                    <option value="technical">Technical issue</option>
                    <option value="feature">Feature request</option>
                    <option value="account">Account changes (name, email)</option>
                    <option value="delete">Delete my account</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-message">Message</Label>
                  <textarea
                    id="support-message"
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Please describe your issue or request..."
                  />
                </div>
                <Button
                  onClick={() => {
                    const reason = (document.getElementById('support-reason') as HTMLSelectElement)?.value;
                    const message = (document.getElementById('support-message') as HTMLTextAreaElement)?.value;
                    if (!reason) {
                      toast({
                        title: "Please select a reason",
                        description: "Choose what you need help with.",
                        variant: "destructive",
                      });
                      return;
                    }
                    if (!message.trim()) {
                      toast({
                        title: "Please enter a message",
                        description: "Tell us how we can help.",
                        variant: "destructive",
                      });
                      return;
                    }
                    // Create mail document for support request
                    addDoc(collection(db, 'mail'), {
                      to: 'support@normiz.com',
                      message: {
                        subject: `Support Request: ${reason} - ${auth.currentUser?.email}`,
                        text: `From: ${auth.currentUser?.email}\nUser ID: ${auth.currentUser?.uid}\nReason: ${reason}\n\nMessage:\n${message}`,
                        html: `<p><strong>From:</strong> ${auth.currentUser?.email}</p><p><strong>User ID:</strong> ${auth.currentUser?.uid}</p><p><strong>Reason:</strong> ${reason}</p><hr/><p><strong>Message:</strong></p><p>${message.replace(/\n/g, '<br/>')}</p>`,
                      },
                    }).then(() => {
                      toast({
                        title: "Request Submitted",
                        description: "Our support team will get back to you soon.",
                      });
                      // Clear the form
                      (document.getElementById('support-reason') as HTMLSelectElement).value = '';
                      (document.getElementById('support-message') as HTMLTextAreaElement).value = '';
                    }).catch(() => {
                      toast({
                        title: "Error",
                        description: "Failed to submit request. Please try again.",
                        variant: "destructive",
                      });
                    });
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Submit Request
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Configure which emails you receive</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Permit Updates</Label>
                    <p className="text-sm text-muted-foreground">Receive emails when permit status changes</p>
                  </div>
                  <Switch
                    checked={notifications.emailJobUpdates}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, emailJobUpdates: checked }))}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Inspection Reminders</Label>
                    <p className="text-sm text-muted-foreground">Get reminded about upcoming inspections</p>
                  </div>
                  <Switch
                    checked={notifications.emailInspectionReminders}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, emailInspectionReminders: checked }))}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Compliance Alerts</Label>
                    <p className="text-sm text-muted-foreground">Important compliance and license expiration alerts</p>
                  </div>
                  <Switch
                    checked={notifications.emailComplianceAlerts}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, emailComplianceAlerts: checked }))}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Weekly Summary</Label>
                    <p className="text-sm text-muted-foreground">Weekly digest of your activity and upcoming tasks</p>
                  </div>
                  <Switch
                    checked={notifications.emailWeeklySummary}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, emailWeeklySummary: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Push Notifications</CardTitle>
                <CardDescription>Browser and mobile push notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Enable Push Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive real-time notifications in your browser</p>
                  </div>
                  <Switch
                    checked={notifications.pushEnabled}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, pushEnabled: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveNotifications} disabled={savingNotifications}>
              {savingNotifications ? "Saving..." : "Save Notification Preferences"}
            </Button>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>Customize how the app looks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Label>Theme</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      variant={theme === 'light' ? 'default' : 'outline'}
                      className="w-full"
                      onClick={() => setTheme('light')}
                    >
                      <Sun className="h-4 w-4 mr-2" />
                      Light
                    </Button>
                    <Button
                      variant={theme === 'dark' ? 'default' : 'outline'}
                      className="w-full"
                      onClick={() => setTheme('dark')}
                    >
                      <Moon className="h-4 w-4 mr-2" />
                      Dark
                    </Button>
                    <Button
                      variant={theme === 'system' ? 'default' : 'outline'}
                      className="w-full"
                      onClick={() => setTheme('system')}
                    >
                      <Monitor className="h-4 w-4 mr-2" />
                      System
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </div>
  );
};

export default Profile;
